import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { createChallenge, type ChallengeTargetType } from "../../api/groups";
import { fetchCollections } from "../../api/collections";
import { fetchRegions } from "../../api/regions";
import { Button } from "../../components/Button";
import {
  ErrorLine,
  INPUT_STYLE,
  LABEL_STYLE,
  Segmented,
  Switch,
  addDays,
  daysBetween,
  groupErrorMessage,
  isUpgradeRequired,
  londonToday,
} from "./group-ui";

// Start a challenge (spec 32): group admins who are Pro. The API answers 402
// otherwise; this form is only offered to admins and shows the Pro note to
// free admins instead.

const TARGETS: Array<{ value: ChallengeTargetType; label: string }> = [
  { value: "collection", label: "Collection" },
  { value: "region", label: "Region" },
  { value: "anything", label: "Anything" },
  { value: "new_ground", label: "New ground" },
];

const TARGET_HINTS: Record<ChallengeTargetType, string> = {
  collection: "Most hills, landmarks or trail sections from one collection.",
  region: "Most hills, landmarks or trail sections inside one region.",
  anything: "Most hills, landmarks or trail sections, anywhere.",
  new_ground: "Most new explored squares on the map.",
};

const MAX_DAYS = 366;

function Field({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <label htmlFor={htmlFor} style={LABEL_STYLE}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>{hint}</span>}
    </div>
  );
}

export function ProChallengeNote() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
      <p style={{ fontSize: 13.5, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
        Starting a challenge needs a group admin with Pro. Everything else in the group keeps
        working on the free plan, and the owner can make a Pro member an admin.
      </p>
      <Link to="/plan" style={{ fontSize: 13, fontWeight: 600, color: "var(--text-accent)" }}>
        See Pro
      </Link>
    </div>
  );
}

export function CreateChallengeForm({
  groupId,
  memberCount,
  onCancel,
}: {
  groupId: string;
  memberCount: number;
  onCancel: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = londonToday();

  const [title, setTitle] = React.useState("");
  const [targetType, setTargetType] = React.useState<ChallengeTargetType>("collection");
  const [targetSlug, setTargetSlug] = React.useState("");
  const [startsOn, setStartsOn] = React.useState(today);
  const [endsOn, setEndsOn] = React.useState(addDays(today, 29));
  const [countRepeats, setCountRepeats] = React.useState(false);
  const [prizeRequest, setPrizeRequest] = React.useState("");

  const collectionsQuery = useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
    enabled: targetType === "collection",
  });
  const regionsQuery = useQuery({
    queryKey: ["regions"],
    queryFn: fetchRegions,
    enabled: targetType === "region",
  });

  const collections = React.useMemo(
    () => [...(collectionsQuery.data?.collections ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [collectionsQuery.data],
  );
  const regions = React.useMemo(
    () => [...(regionsQuery.data?.regions ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [regionsQuery.data],
  );

  const needsSlug = targetType === "collection" || targetType === "region";
  const repeatsApply = targetType !== "new_ground";
  const prizeAllowed = memberCount >= 3;

  const length = daysBetween(startsOn, endsOn);
  let dateError: string | null = null;
  if (!startsOn || !endsOn) dateError = "Pick a start and an end date.";
  else if (length < 0) dateError = "The end date is before the start.";
  else if (length > MAX_DAYS) dateError = "A challenge can run for a year at most.";

  const mutation = useMutation({
    mutationFn: () =>
      createChallenge(groupId, {
        title: title.trim(),
        targetType,
        targetSlug: needsSlug ? targetSlug : null,
        startsOn,
        endsOn,
        countRepeats: repeatsApply ? countRepeats : false,
        prizeRequest: prizeAllowed && prizeRequest.trim() ? prizeRequest.trim() : null,
      }),
    onSuccess: async ({ challengeId }) => {
      await queryClient.invalidateQueries({ queryKey: ["groups"] });
      void navigate({ to: "/groups/$id/challenges/$cid", params: { id: groupId, cid: challengeId } });
    },
  });

  if (mutation.isError && isUpgradeRequired(mutation.error)) return <ProChallengeNote />;

  const canSubmit = title.trim().length > 0 && (!needsSlug || !!targetSlug) && !dateError && !mutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <Field label="Title" htmlFor="challenge-title">
        <input
          id="challenge-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={80}
          placeholder="Wainwrights before Christmas"
          style={INPUT_STYLE}
          autoFocus
        />
      </Field>

      <Field label="What counts" hint={TARGET_HINTS[targetType]}>
        <Segmented
          label="Challenge target"
          value={targetType}
          options={TARGETS}
          onChange={(v) => {
            setTargetType(v);
            setTargetSlug("");
          }}
        />
      </Field>

      {targetType === "collection" && (
        <Field label="Collection" htmlFor="challenge-collection">
          <select
            id="challenge-collection"
            value={targetSlug}
            onChange={(e) => setTargetSlug(e.target.value)}
            style={INPUT_STYLE}
            disabled={collectionsQuery.isLoading}
          >
            <option value="">{collectionsQuery.isLoading ? "Loading…" : "Choose a collection"}</option>
            {collections.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name} ({c.itemCount.toLocaleString("en-GB")})
              </option>
            ))}
          </select>
        </Field>
      )}

      {targetType === "region" && (
        <Field label="Region" htmlFor="challenge-region">
          <select
            id="challenge-region"
            value={targetSlug}
            onChange={(e) => setTargetSlug(e.target.value)}
            style={INPUT_STYLE}
            disabled={regionsQuery.isLoading}
          >
            <option value="">{regionsQuery.isLoading ? "Loading…" : "Choose a region"}</option>
            {regions.map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <Field label="Starts" htmlFor="challenge-start">
          <input
            id="challenge-start"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            style={INPUT_STYLE}
          />
        </Field>
        <Field label="Ends" htmlFor="challenge-end">
          <input
            id="challenge-end"
            type="date"
            value={endsOn}
            min={startsOn}
            max={startsOn ? addDays(startsOn, MAX_DAYS) : undefined}
            onChange={(e) => setEndsOn(e.target.value)}
            style={INPUT_STYLE}
          />
        </Field>
      </div>
      <span className="tnum" style={{ fontSize: 12, color: dateError ? "var(--status-danger)" : "var(--text-muted)", marginTop: -6 }}>
        {dateError ?? `${length + 1} day${length === 0 ? "" : "s"}, both dates included (UK time).`}
      </span>

      {repeatsApply && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            padding: "12px 14px",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
            <span
              id="challenge-repeats-label"
              onClick={() => setCountRepeats((v) => !v)}
              style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", cursor: "pointer" }}
            >
              Repeat visits count
            </span>
            <span id="challenge-repeats-hint" style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>
              Applies to hills and landmarks: one you have been to before counts again if you reach it
              during the challenge, once per person. Trail sections only count the first time you
              complete them.
            </span>
          </span>
          <Switch
            checked={countRepeats}
            onChange={setCountRepeats}
            labelledBy="challenge-repeats-label"
            describedBy="challenge-repeats-hint"
          />
        </div>
      )}

      {prizeAllowed && (
        <Field
          label="Ask Atlas for a prize (optional)"
          htmlFor="challenge-prize"
          hint="Say what the challenge is for. An Atlas admin decides, and nothing is granted until the results have been checked."
        >
          <textarea
            id="challenge-prize"
            value={prizeRequest}
            onChange={(e) => setPrizeRequest(e.target.value)}
            maxLength={500}
            rows={2}
            style={{ ...INPUT_STYLE, height: "auto", padding: 10, resize: "vertical" }}
          />
        </Field>
      )}

      {mutation.isError && <ErrorLine>{groupErrorMessage(mutation.error, "Couldn't start the challenge. Try again.")}</ErrorLine>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", paddingTop: 4 }}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {mutation.isPending ? "Starting…" : "Start challenge"}
        </Button>
      </div>
    </form>
  );
}
